import type { BaseMasterRecord } from '@/features/masters';
import type { HierarchyScope, HierarchyScopeView } from './hierarchyScope';

/**
 * The Templates feature's wire types, mirroring
 * `productplatformbackend/docs/api/TEMPLATE-API.md`.
 *
 * One record shape for all three kinds, because one table and one controller
 * serve all three -- they differ in the default sections and fields the client
 * posts, not in anything the server stores.
 */

/** The discriminator. Three screens, three values, one endpoint (the DEFAULT kind was removed on 2026-10-16). */
export type TemplateKind = 'RFQ' | 'PURCHASE_REQUEST' | 'SUPPLIER_ONBOARDING';

/**
 * Every field type a template may carry.
 *
 * Two sets, and the distinction matters: the first nine are what the builder's
 * "Data type" select offers when someone adds a custom field; the rest are
 * declared by the kinds' own system fields and can never be chosen. A union
 * covering only the nine would make a Supplier Onboarding template unstorable
 * -- its Registered Address has nowhere to go.
 */
export type TemplateFieldType =
  /* the nine a user may choose */
  | 'Text'
  | 'Long text'
  | 'Number'
  | 'Date'
  | 'Dropdown'
  | 'Search and Select'
  | 'Checkbox'
  | 'Radio'
  | 'File upload'
  /* an On / Off switch (design21, 2026-10-22) -- also the record's own Status system field */
  | 'Toggle'
  /* declared by the kinds' system fields, never selectable */
  | 'Short text'
  | 'Email'
  | 'Phone'
  | 'URL'
  | 'Currency'
  | 'Masked text'
  | 'Address block'
  | 'Multi-select'
  | 'Multi-select Dropdown'
  | 'Multi-select + file'
  | 'Calculated';

/** The ten the custom-field select offers, alphabetically with Toggle last (design21, 2026-10-22), and their notes. */
export const CUSTOM_FIELD_TYPES: ReadonlyArray<{
  readonly label: TemplateFieldType;
  readonly note?: string;
}> = [
  { label: 'Checkbox' },
  { label: 'Date' },
  { label: 'Dropdown' },
  { label: 'File upload' },
  { label: 'Long text', note: 'Up to 500 characters' },
  { label: 'Number', note: 'Up to 12 digits' },
  { label: 'Radio' },
  { label: 'Search and Select', note: 'Type to search, pick one from the results' },
  { label: 'Text', note: 'Up to 100 characters' },
  { label: 'Toggle', note: 'On / Off switch' },
] as const;

/** One field of one section, as stored. */
export interface TemplateField {
  id?: number;
  /** Identifies the field within its section, and survives a relabel. */
  fieldKey: string;
  label: string;
  fieldType: TemplateFieldType;
  helperText?: string | null;
  /** True for a field the kind declares: locked, not removable. */
  system: boolean;
  mandatory: boolean;
  /** The Conditional tag. Only Supplier Onboarding sets it. */
  conditional: boolean;
  /** Only the RFQ product step declares groups. Scoped to its section. */
  fieldGroup?: string | null;
  ordinal?: number;
  /**
   * The field's own lifecycle, independent of the template's: Active or Inactive.
   *
   * Returned on every detail read. A runtime form renders only the Active ones --
   * a retired field keeps its row but is not part of the form any more.
   */
  statusId?: number | null;
  statusName?: string | null;
  /**
   * The values a choice field offers, in the author's order -- Dropdown, Search
   * and Select, Radio and the two multi-selects only (`typeTakesOptions`).
   *
   * Empty or absent means none are configured yet; the runtime form then
   * collects that field as free text, which is what it did before options
   * existed.
   */
  options?: string[] | null;
  /**
   * The on/off of the BID template's mixed-sourcing toggle, which the detail lists as its Add Product and PR
   * section's first field (`MIXED_SOURCING_FIELD_KEY`, no row id). Absent on every other field.
   */
  value?: boolean | null;
}

/** The detail's field for the BID template's "Allow mixed sourcing (PR + Direct) per bid" (review sheet #31). */
export const MIXED_SOURCING_FIELD_KEY = 'allow-mixed-sourcing';

/**
 * How Initiators add products to a BID made from a BID template (2026-10-21): only from approved Purchase Requests,
 * only directly from the Product master, or both in one BID. The template's `productSourcing`.
 */
export type ProductSourcing = 'WITH_PR' | 'WITHOUT_PR' | 'MIXED';

/** The three, in the order the builder draws them. */
export const PRODUCT_SOURCINGS: readonly ProductSourcing[] = ['WITH_PR', 'WITHOUT_PR', 'MIXED'];

/**
 * What a stored BID template without a Product Sourcing reads as (one saved before the choice existed): Mixed. A new
 * template starts on `NEW_TEMPLATE_PRODUCT_SOURCING` instead.
 */
export const DEFAULT_PRODUCT_SOURCING: ProductSourcing = 'MIXED';

/** A new BID template's Product Sourcing, and the server's when a create leaves it out (2026-10-22, once Mixed). */
export const NEW_TEMPLATE_PRODUCT_SOURCING: ProductSourcing = 'WITH_PR';

/** Each Product Sourcing's words, as the reference has them. */
export const PRODUCT_SOURCING_LABELS: Readonly<Record<ProductSourcing, string>> = {
  WITH_PR: 'With Purchase Request',
  WITHOUT_PR: 'Without Purchase Request',
  MIXED: 'Mixed Sourcing (Purchase Request + Direct Product)',
};

/** What each Product Sourcing means, under its words on the builder's radio card. */
export const PRODUCT_SOURCING_HINTS: Readonly<Record<ProductSourcing, string>> = {
  WITH_PR: 'Products come only from approved Purchase Requests.',
  WITHOUT_PR: 'Initiators add products directly from the Product master.',
  MIXED: 'A single BID can combine Purchase Request products and directly added products.',
};

export function isProductSourcing(value: unknown): value is ProductSourcing {
  return value === 'WITH_PR' || value === 'WITHOUT_PR' || value === 'MIXED';
}

/**
 * A template's Product Sourcing: its own `productSourcing`, else Mixed. A template stored before the choice existed
 * answers only `allowMixedSourcing` (or lists the old toggle among its fields); true or false, it reads Mixed
 * (2026-10-21).
 */
export function productSourcingOf(template: Pick<TemplateDetail, 'productSourcing'>): ProductSourcing {
  return isProductSourcing(template.productSourcing) ? template.productSourcing : DEFAULT_PRODUCT_SOURCING;
}

/**
 * The types whose answer is picked from `options`.
 *
 * The backend's `TemplateFieldType.takesOptions()`, spelled here so the builder
 * knows when to show the options editor and the form knows when to draw a list.
 */
export const OPTION_FIELD_TYPES: ReadonlySet<string> = new Set<TemplateFieldType>([
  'Dropdown',
  'Search and Select',
  'Radio',
  'Multi-select',
  'Multi-select Dropdown',
]);

/** Whether a field of this type carries a configured option list. */
export function typeTakesOptions(fieldType: string): boolean {
  return OPTION_FIELD_TYPES.has(fieldType);
}

/** The server's bounds on a field's option list (`TemplateFieldRequest`). */
export const TEMPLATE_OPTION_LIMITS = { maxOptions: 100, maxLength: 200 } as const;

/** One step of the builder, as stored. */
export interface TemplateSection {
  id?: number;
  /** Stable across a retitle -- what lets an update keep the section's fields. */
  sectionKey: string;
  title: string;
  ordinal?: number;
  /** May be empty: the PR kind's "Custom fields" step starts that way. */
  fields: TemplateField[];
}

/** A listing row. */
export interface TemplateRecord extends BaseMasterRecord {
  kind: TemplateKind;
  name: string;
  /**
   * The server's Template Code (`PRT-000001`, `SOT-000001`), assigned on create and never changed. A column on the
   * PR Template and Supplier Onboarding lists, and named in "Template <code> updated successfully".
   */
  templateCode?: string | null;
  description?: string | null;
  /** Only the RFQ screen shows this as a column; only it collects one. */
  templateCategory?: string | null;
  /** Derived server-side from the stored rows, so it cannot drift. */
  fieldCount: number;
  /**
   * The builder's Status toggle as the author last left it.
   *
   * Separate from `statusName` because a draft save writes Draft, which would
   * otherwise overwrite "the author wants this active" and reopen the builder
   * showing Inactive. `null` on rows that predate the column; the builder then
   * falls back to the status.
   */
  activeIntent?: boolean | null;
  /**
   * Who `createdBy` is, resolved server-side through `UserDirectory`.
   *
   * Declared here rather than on `BaseMasterRecord`: every master has the same
   * gap and will want the same field, but widening a type eighteen screens share
   * is their change to make, not this feature's.
   *
   * Null where the id resolves to nobody -- a deleted account. The cell then
   * shows an em dash rather than the id: "User 11" reads like an answer and is
   * really the absence of one.
   *
   * "By system" where `createdBy` itself is null: a template the platform seeded
   * (NB-533). The cell keys on `createdBy`, not on this text.
   */
  createdByName?: string | null;
  /** On the active template cards only (NB-355): the sections that hold fields, and how many forms used it. */
  sectionCount?: number | null;
  usedCount?: number | null;
  /** A PR Template's Category master row before it named several (an older server's); `categoryIds` supersedes it. */
  categoryId?: number | null;
  /**
   * The template's Category master rows: a PR Template's (2026-10-09; `templateCategory` is their names joined by ", ")
   * or a Supplier Onboarding template's (2026-10-08); empty on the other kinds.
   */
  categoryIds?: number[] | null;
  categories?: Array<{ id: number; name: string }> | null;
}

/** A PR Template's Category master rows: `categoryIds`, or the lone `categoryId` an older server sends. */
export function templateCategoryIdsOf(record: Pick<TemplateRecord, 'categoryId' | 'categoryIds'>): number[] {
  if (record.categoryIds && record.categoryIds.length > 0) return [...record.categoryIds];
  return record.categoryId != null ? [record.categoryId] : [];
}

/** A PR Template's category names, one per tag: `categories`, or the stored `templateCategory` as one. */
export function templateCategoryNamesOf(record: Pick<TemplateRecord, 'categories' | 'templateCategory'>): string[] {
  if (record.categories && record.categories.length > 0) return record.categories.map((category) => category.name);
  return record.templateCategory ? [record.templateCategory] : [];
}

/** What a template code starts with, by kind -- the server's `TemplateKind.templateCodePrefix`. */
export const TEMPLATE_CODE_PREFIX: Readonly<Record<TemplateKind, string>> = {
  RFQ: 'BIDT',
  PURCHASE_REQUEST: 'PRT',
  SUPPLIER_ONBOARDING: 'SOT',
};

/** A Category master row a PR Template is mapped to, labelled `CODE - Name`. */
export interface TemplateCategoryOption {
  id: number;
  code?: string | null;
  name: string;
  label: string;
}

/** One template in full -- what the builder seeds every step from. */
export interface TemplateDetail extends TemplateRecord {
  sections: TemplateSection[];
  /** The entity the template was created under, and its Business Unit, Division and Department (ids). */
  legalEntityId?: number | null;
  businessUnitId?: number | null;
  divisionId?: number | null;
  departmentId?: number | null;
  /** A hierarchy-mapped template's (PR Template, Supplier Onboarding) mapped Entities, Business Units, Divisions and Departments, labelled. Empty on the other kinds. */
  scope?: HierarchyScopeView | null;
  /**
   * A BID template's "Allow mixed sourcing (PR + Direct) per bid" (review sheet #31): whether one BID made from it may
   * hold products from Purchase Requests beside products added without a PR. False on the other kinds. Since
   * 2026-10-21 it equals `productSourcing === 'MIXED'`.
   */
  allowMixedSourcing?: boolean;
  /** A BID template's Product Sourcing (2026-10-21). Absent on a template stored before it, and on the other kinds. */
  productSourcing?: ProductSourcing | null;
  /**
   * A BID template's "Allow suppliers to withdraw" (2026-10-22): off, a supplier must quote -- the intent's Withdrawn
   * answer and its reason leave the Supplier Bid Form. Absent on the other kinds; a stored BID template without it
   * reads true.
   */
  allowSupplierWithdraw?: boolean | null;
}

/** A Business Unit, Division or Department as the builder's dropdowns list it. */
export interface TemplateOrgUnit {
  id: number;
  name: string;
}

/** The create and update body, which are the same shape. */
export interface TemplatePayload {
  kind: TemplateKind;
  name: string;
  description: string | null;
  templateCategory: string | null;
  /** The template's Business Unit, Division and Department (the RFQ kind's), picked in cascade. */
  businessUnitId?: number | null;
  divisionId?: number | null;
  departmentId?: number | null;
  /**
   * The template's Category master rows, sent whole: a PR Template's (at least one) or a Supplier Onboarding
   * template's; the server names them. Left out by the other kinds.
   */
  categoryIds?: number[];
  /**
   * A name, never an id: 'Active', 'Inactive' or 'Draft'.
   *
   * The screens and `os_status_master` both say "Draft"; the API
   * speaks the stored name and accepts either, so `TEMPLATE_STATUS` below maps
   * the label the screen shows to the value the wire carries.
   */
  statusName: string;
  /** The Status toggle, remembered across a draft save. */
  activeIntent: boolean;
  /** `productSourcing === 'MIXED'`, still sent beside it (review sheet #31). Left out by the other kinds. */
  allowMixedSourcing?: boolean;
  /**
   * The BID template's Product Sourcing (2026-10-21). Left out by the other kinds; a create without it is With
   * Purchase Request (2026-10-22), an update without it keeps the stored one.
   */
  productSourcing?: ProductSourcing;
  /**
   * The BID template's "Allow suppliers to withdraw" (2026-10-22). Left out by the other kinds; a create without it
   * is true, an update without it keeps the stored one.
   */
  allowSupplierWithdraw?: boolean;
  /**
   * A hierarchy-mapped template's hierarchy mapping, replacing the stored one. Left out by the other kinds; the server
   * keeps a stored mapping when it is absent.
   */
  scope?: HierarchyScope;
  sections: Array<{
    sectionKey: string;
    title: string;
    fields: Array<{
      fieldKey: string;
      label: string;
      fieldType: TemplateFieldType;
      helperText: string | null;
      system: boolean;
      mandatory: boolean;
      conditional: boolean;
      fieldGroup: string | null;
      /** Sent for choice types only; the server ignores and clears it on any other. */
      options?: string[];
    }>;
  }>;
}

/** One template the import refused, and why. */
export interface TemplateImportRejection {
  /** The line in the file as a person reading it would count, header included. */
  line: number;
  /** The template's name where one could be read. */
  code?: string | null;
  reason: string;
}

/** What an import did, template by template. */
export interface TemplateImportResult {
  read: number;
  imported: number;
  rejected: TemplateImportRejection[];
}

/**
 * The statuses, as the wire spells them and as the screen shows them.
 *
 * Four, where every master has three: Draft is a state the builder writes on
 * two separate paths -- "Save as Draft" always, and a completed save with the
 * status toggle off.
 */
export const TEMPLATE_STATUS = {
  active: 'Active',
  draft: 'Draft',
  inactive: 'Inactive',
  deleted: 'Deleted',
} as const;

/** What the status control and the status pill show for a stored status name. */
export const TEMPLATE_STATUS_LABEL: Readonly<Record<string, string>> = {
  Active: 'Active',
  Draft: 'Draft',
  Inactive: 'Inactive',
  Deleted: 'Deleted',
};

/**
 * What each kind is called where kinds appear side by side.
 *
 * The sub-menu names as they were, so a card that says "Type: BID Template"
 * leads to the screen the menu calls BID Template. The sidebar and breadcrumb
 * read "Purchase Request Template" and "Supplier Onboarding Template" for the
 * other two (2026-10-28); these labels kept the short names.
 */
export const TEMPLATE_KIND_LABELS: Readonly<Record<TemplateKind, string>> = {
  RFQ: 'BID Template',
  PURCHASE_REQUEST: 'PR Template',
  SUPPLIER_ONBOARDING: 'Supplier Onboarding',
};

/** The status select's options: "All Status" plus the three a user may pick. */
export const TEMPLATE_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All Status' },
  { value: TEMPLATE_STATUS.active, label: 'Active' },
  { value: TEMPLATE_STATUS.inactive, label: 'Inactive' },
  { value: TEMPLATE_STATUS.draft, label: 'Draft' },
] as const;

/** The four the Export dialog offers, and the `format` the endpoint reads. */
export type TemplateExportFormat = 'xlsx' | 'csv' | 'pdf' | 'json';

/**
 * The Export dialog's cards, in the reference's own order, wording and icons.
 *
 * Here rather than inline in the toolbar because the download's filename and its
 * extension are decided from the same row that drew the card -- two places
 * naming the same four formats would eventually disagree about one of them.
 */
export const TEMPLATE_EXPORT_OPTIONS: ReadonlyArray<{
  format: TemplateExportFormat;
  icon: string;
  label: string;
  hint: string;
}> = [
  { format: 'xlsx', icon: 'pi-table', label: 'Excel (.xlsx)', hint: 'Formatted workbook' },
  { format: 'csv', icon: 'pi-file', label: 'CSV (.csv)', hint: 'Comma separated, UTF-8' },
  { format: 'pdf', icon: 'pi-file-pdf', label: 'PDF (.pdf)', hint: 'Print-ready table' },
  { format: 'json', icon: 'pi-code', label: 'JSON (.json)', hint: 'For integrations' },
] as const;
